import axios from "axios";
import Constants from "../constants";

// Create axios instance with base configuration
const api = axios.create({
  baseURL: Constants.PY_API_URL,
  timeout: 30000, // Increased from 10000 to 30000 (30 seconds)
  headers: {
    "Content-Type": "application/json",
    version: Constants.API_VERSION,
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const serverDownStatuses = [500, 502, 503, 504];
    // Handle different types of errors that should trigger logout
    const shouldLogout =
      error.response?.status === 401 || // Unauthorized
      error.response?.status === 403 || // Forbidden
      error.code === "ERR_NETWORK" || // Network error (server unreachable)
      error.code === "ERR_INTERNET_DISCONNECTED" || // No internet
      error.code === "ERR_CONNECTION_REFUSED" || // Connection refused
      error.code === "ERR_CONNECTION_TIMEOUT" || // Connection timeout
      error.code === "ERR_NAME_NOT_RESOLVED"; // DNS resolution failed

    if (shouldLogout) {
      // Clear only the essential auth data
      localStorage.removeItem("accessToken");
      localStorage.removeItem("isLoggedin");
      localStorage.removeItem("userDetails");

      // Navigate to login page
      window.location.href = "/login";
    }
    if (serverDownStatuses.includes(status)) {
      window.location.href = "/serverdown";
      return; // Prevent further error handling
    }
    return Promise.reject(error);
  }
);

// Auth API methods
export const authAPI = {
  // Login API - same endpoint as Angular
  login: async (email: string, password: string) => {
    const requestBody = {
      email,
      password,
      isregistered: Constants.IS_REGISTERED,
      admincreated: Constants.ADMIN_CREATED,
      masterdetailId: Constants.MASTER_DETAIL_ID,
    };

    return api.post("/api/users/login", requestBody);
  },

  // Forgot Password API
  forgotPassword: async (email: string) => {
    return api.post("/api/auth/forgot-password", { email });
  },

  // Reset Password API
  resetPassword: async (token: string, newPassword: string) => {
    return api.post("/api/auth/reset-password", {
      token,
      new_password: newPassword,
    });
  },

  // Change Password API
  changePassword: async (oldPassword: string, newPassword: string) => {
    return api.post("/api/auth/change-password", {
      old_password: oldPassword,
      new_password: newPassword,
    });
  },

  // Get logo details
  getLogoDetails: async () => {
    return api.get(
      `/api/settings/getLogo?filter[where][masterdetailId]=${Constants.MASTER_DETAIL_ID}`
    );
  },
};

// Category API methods
export const categoryAPI = {
  exportCategories: async () => {
    return api.get("/api/v2/categories/exportCategories", {
      responseType: "blob",
    });
  },

  exportSubCategories: async (categoryId: string) => {
    return api.get(
      `/api/v2/categories/exportSubCategories?parentId=${categoryId}`,
      { responseType: "blob" }
    );
  },

  // Get all categories with media
  getCategories: async (params?: {
    page?: number;
    items_per_page?: number;
    search?: string;
    "where[categorystatus]"?: number;
    "where[totalsubcategories]"?: number;
    sort?: string;
  }) => {
    const queryParams = new URLSearchParams();

    // Add pagination parameters
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.items_per_page)
      queryParams.append("items_per_page", params.items_per_page.toString());

    // Add search parameter (Category Name)
    if (params?.search) queryParams.append("search", params.search);

    // Add status filter
    if (params?.["where[categorystatus]"] !== undefined) {
      queryParams.append(
        "where[categorystatus]",
        params["where[categorystatus]"].toString()
      );
    }

    // Add totalsubcategories filter
    if (params?.["where[totalsubcategories]"] !== undefined) {
      queryParams.append(
        "where[totalsubcategories]",
        params["where[totalsubcategories]"].toString()
      );
    }

    // Add sort parameter
    if (params?.sort) queryParams.append("sort", params.sort);

    const queryString = queryParams.toString();
    const url = `/api/v2/categories${queryString ? `?${queryString}` : ""}`;

    return api.get(url);
  },

  // Get active categories
  getActiveCategories: async () => {
    return api.get("/api/v2/categories?where[categorystatus]=1");
  },

  // Get deactive categories count
  getDeactiveCategories: async () => {
    return api.get("/api/v2/categories?where[categorystatus]=0");
  },

  // Get total categories count
  getTotalCategories: async () => {
    return api.get("/api/v2/categories?where[isTotal]=1");
  },

  // Get single category details with media
  getCategoryDetails: async (categoryId: string) => {
    return api.get(
      `/api/categories/${categoryId}?filter[include]=categorymedia`
    );
  },

  // Get subcategories for a specific category
getSubcategories: async (categoryId: string) => {
  return api.get(
    `/api/categories?filter[where][isCategory]=0&isWeb=true&filter[include]=categorymedia&filter[where][parentId]=${categoryId}&filter[where][categorystatus]=1`
  );
},

  // Get subcategories listing with pagination, search, and filtering
  getSubcategoriesListing: async (params?: {
    page?: number;
    items_per_page?: number;
    search?: string;
    "where[categorystatus]"?: number;
    "where[parentId]"?: string;
    "where[totalproducts]"?: number;
    sort?: string; // Added sort parameter
  }) => {
    const queryParams = new URLSearchParams();

    // Add pagination parameters
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.items_per_page)
      queryParams.append("items_per_page", params.items_per_page.toString());

    // Add search parameter
    if (params?.search) queryParams.append("search", params.search);

    // Add status filter
    if (params?.["where[categorystatus]"] !== undefined) {
      queryParams.append(
        "where[categorystatus]",
        params["where[categorystatus]"].toString()
      );
    }

    // Add parentId filter (required for subcategories)
    if (params?.["where[parentId]"]) {
      queryParams.append("where[parentId]", params["where[parentId]"]);
    }

    // Add total products filter
    if (params?.["where[totalproducts]"] !== undefined) {
      queryParams.append(
        "where[totalproducts]",
        params["where[totalproducts]"].toString()
      );
    }

    // Add sort parameter
    if (params?.sort) queryParams.append("sort", params.sort);

    const queryString = queryParams.toString();
    const url = `/api/v2/subcategories${queryString ? `?${queryString}` : ""}`;

    return api.get(url);
  },

  // Get single subcategory details with media and category details
  getSubcategoryDetails: async (subcategoryId: string) => {
    return api.get(
      `/api/categories/${subcategoryId}?filter[include]=categorymedia&filter[include]=categorydetail`
    );
  },

  // Get products for a specific subcategory
  getSubcategoryProducts: async (subcategoryId: string) => {
    return api.get(
      `/api/products?filter[where][categoryId]=${subcategoryId}&filter[include]=productmedia`
    );
  },

  // Upload category media
  uploadCategoryMedia: async (formData: FormData) => {
    return api.post(
      `/api/containers/productmedia-${Constants.USER_LOGGED_BY_CODE}/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  },

  // Add new category
  addCategory: async (categoryData: any) => {
    return api.post("/api/v2/categories", categoryData);
  },

  // Update category
  updateCategory: async (categoryId: string, categoryData: any) => {
    return api.put(`/api/v2/categories/${categoryId}`, categoryData);
  },

  // Delete subcategory
  deleteSubcategory: async (subcategoryId: string) => {
    return api.delete(`/api/categories/${subcategoryId}`);
  },

  // Delete category
  deleteCategory: async (categoryId: string) => {
    return api.delete(`/api/categories/${categoryId}`);
  },

  // Get media download URL
  getMediaDownloadUrl: (filename: string) => {
    return `${Constants.PY_API_URL}api/containers/productmedia-${Constants.USER_LOGGED_BY_CODE}/download/${filename}`;
  },
};

// Users API methods
export const usersAPI = {
  // Get users listing with includes
  getUsers: async (params?: {
    page?: number;
    items_per_page?: number;
    search?: string;
    "where[roleId]"?: number;
    "where[userstatus]"?: number;
    "where[groupId]"?: string;
    sort?: string;
  }) => {
    const queryParams = new URLSearchParams();

    // Pagination
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.items_per_page)
      queryParams.append("items_per_page", params.items_per_page.toString());
    if (params?.search) queryParams.append("search", params.search);

    // Role filter (default to 2)
    queryParams.append(
      "where[roleId]",
      (params?.["where[roleId]"] ?? 2).toString()
    );

    // Group filter
    if (params?.["where[groupId]"]) {
      queryParams.append("where[groupId]", params["where[groupId]"]);
    }

    // Status filter
    if (params?.["where[userstatus]"] !== undefined) {
      queryParams.append(
        "where[userstatus]",
        params["where[userstatus]"].toString()
      );
    }
    // Sort
    if (params?.sort) queryParams.append("sort", params.sort);

    const queryString = queryParams.toString();
    const url = `/api/v2/users${queryString ? `?${queryString}` : ""}`;
    return api.get(url);
  },

  // Get user details by user ID
  getUserById: async (userId: string) => {
    return api.get(`/api/users/${userId}`);
  },

  // Get active users count
  getActiveUsers: async () => {
    return api.get(`/api/v2/users?where[userstatus]=1&where[roleId]=2`);
  },

  getstatics : async () => {
    return api.get(`/api/v2/users/userstats/`);
  },

  // Get deactive users count
  getDeactiveUsers: async () => {
    return api.get(`/api/v2/users?where[userstatus]=0&where[roleId]=2`);
  },

  // Get total users count
  getTotalUsers: async () => {
    return api.get(`/api/v2/users?where[roleId]=2`);
  },

  getRecentUsers: async () => {
    return api.get(`/api/v2/users?where[roleId]=2&sort=created DESC&page=1&items_per_page=5`);
  },

  updateUser : async (userId: string, userData: any) => {
    return api.put(`/api/users/${userId}`, userData);
  }
};

// Groups API
export const groupsAPI = {
  // Main listing API with pagination (v2)
  getGroups: async (params?: {
    page?: number;
    items_per_page?: number;
    search?: string;
    "where[status]"?: number;
    sort?: string;
  }) => {
    const queryParams = new URLSearchParams();

    // Pagination
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.items_per_page)
      queryParams.append("items_per_page", params.items_per_page.toString());

    // Search
    if (params?.search) queryParams.append("search", params.search);

    // Status filter
    if (params?.["where[status]"] !== undefined) {
      queryParams.append("where[status]", params["where[status]"].toString());
    }

    // Sort
    if (params?.sort) queryParams.append("sort", params.sort);

    const queryString = queryParams.toString();
    const url = `/api/groups${queryString ? `?${queryString}` : ""}`;
    return api.get(url);
  },

  // Statistics APIs
  getActiveGroups: () => api.get("/api/groups?where[status]=1"),
  getDeactiveGroups: () => api.get("/api/groups?where[status]=0"),
  getTotalGroups: () => api.get("/api/groups"),

  // CRUD operations
  addGroup: (groupData: any) => api.post("/api/groups", groupData),
  updateGroup: (id: number, groupData: any) =>
    api.patch(`/api/groups/${id}`, groupData),
  deleteGroup: (id: number) => api.delete(`/api/groups/${id}`),
  updateGroupStatus: (id: number | string, status: number | string) =>
    api.post(`/api/groups/status?group_id=${id}&status=${status}`),
};

// Banner API methods
export const bannerAPI = {
  // Get all banners
  getBanners: async () => {
    return api.get("/api/v2/offer-banners");
  },

  // Add new banner
  addBanner: async (bannerData: any) => {
    return api.post("/api/v2/offer-banners", bannerData);
  },

  // Update banner
  updateBanner: async (bannerId: string, bannerData: any) => {
    return api.put(`/api/v2/offer-banners/${bannerId}`, bannerData);
  },

  // Delete banner
  deleteBanner: async (bannerId: string) => {
    return api.delete(`/api/v2/offer-banners/${bannerId}`);
  },
};

// Privacy Policy API methods
export const privacyPolicyAPI = {
  getPrivacyPolicy: async () => {
    return api.get("/api/v2/cms/privacypolicy");
  },

  updatePrivacyPolicy: async (text: string) => {
    return api.put("/api/v2/cms/privacypolicy", { text });
  },
};

export const termsAndConditionsAPI = {
  getTerms: () => api.get("/api/v2/cms/termsandconditions"),
  updateTerms: (content: string) =>
    api.put("/api/v2/cms/termsandconditions", { text: content }),
};

export const aboutRewardAPI = {
  getAboutUs: async () => {
    return api.get("/api/v2/cms/aboutus");
  },
  updateAboutUs: async (content: string) => {
    return api.put("/api/v2/cms/aboutus", { text: content });
  },
};

// Product Details API methods
export const productDetailsAPI = {
  addProductDetailsLabel: async (label: string) => {
    return api.post("/api/v2/product-details", {
      lable: label,
      data: "",
    });
  },

  getProductDetailsLabels: async (skip: number = 0, limit: number = 10) => {
    return api.get(`/api/v2/product-details?skip=${skip}&limit=${limit}`);
  },

  updateProductDetailsLabel: async (id: string, label: string) => {
    return api.put(`/api/v2/product-details/${id}`, {
      lable: label,
      data: "",
    });
  },

  deleteProductDetailsLabel: async (id: string) => {
    return api.delete(`/api/v2/product-details/${id}`);
  },
};

// Product API methods
export const productAPI = {
  exportProducts: async () => {
    return api.get("/api/v2/products/exportProducts", { responseType: "blob" });
  },

  createProduct: async (productData: any) => {
    return api.post("/api/products/", productData);
  },

  uploadProductImages: async (formData: FormData) => {
    return api.post(
      `/api/containers/productmedia-${Constants.USER_LOGGED_BY_CODE}/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  },

  getProducts: async () => {
    return api.get("/api/products/");
  },

  getProductsV2: async (params?: Record<string, any>) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          queryParams.append(key, value.toString());
        }
      });
    }
    const queryString = queryParams.toString();
    const url = `/api/v2/products${queryString ? `?${queryString}` : ""}`;
    return api.get(url);
  },

  getActiveProducts: async () => {
    return api.get(
      "/api/v2/products?where[productstatus]=1&items_per_page=1000"
    );
  },

  getProduct: async (productId: string) => {
    return api.get(`/api/products/${productId}`);
  },

  updateProduct: async (productId: string, productData: any) => {
    return api.put(`/api/products/${productId}`, productData);
  },

  deleteProduct: async (productId: string) => {
    return api.delete(`/api/products/${productId}`);
  },

  getTopSellingProducts: async () => {
    return api.get("/api/products/productTopSelling");
  },

  getNearOutOfStockProducts: async () => {
    return api.get(
      "/api/products/filtered?where[availablequantity][lte]=5&where[productstatus]=1&limit=5"
    );
  },

  updateProductStatus: async (productId: string, productStatus: number) => {
    const params = new URLSearchParams();
    params.append("product_id", productId);
    params.append("product_status", productStatus.toString());

    return api.post(`/api/products/status?${params.toString()}`);
  },
};

// Order API methods
export const orderAPI = {
  getOrdersV2: async (params?: {
    page?: number;
    items_per_page?: number;
    search?: string;
    "where[orderstatus]"?: string;
    "where[totalamount]"?: number;
    "where[totalitems]"?: number;
    "where[created]"?: string;
    "where[city]"?: string;
    "where[agentNumber]"?: string;
    sort?: string;
  }) => {
    const queryParams = new URLSearchParams();

    // Add pagination parameters
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.items_per_page)
      queryParams.append("items_per_page", params.items_per_page.toString());

    // Add search parameter
    if (params?.search) queryParams.append("search", params.search);

    // Add total amount filter
    if (params?.["where[totalamount]"] !== undefined) {
      queryParams.append(
        "where[totalamount]",
        params["where[totalamount]"].toString()
      );
    }
    // Add total items filter
    if (params?.["where[totalitems]"] !== undefined) {
      queryParams.append(
        "where[totalitems]",
        params["where[totalitems]"].toString()
      );
    }

    // Add created at filter
    if (params?.["where[created]"] !== undefined) {
      queryParams.append("where[created]", params["where[created]"]);
    }
    // Add city filter
    if (params?.["where[city]"] !== undefined) {
      queryParams.append("where[city]", params["where[city]"]);
    }

    // Add agent number filter
    if (params?.["where[agentNumber]"] !== undefined) {
      queryParams.append("where[agentNumber]", params["where[agentNumber]"]);
    }

    // Add status filter
    if (params?.["where[orderstatus]"] !== undefined) {
      queryParams.append("where[orderstatus]", params["where[orderstatus]"]);
    }

    // Add sort parameter
    if (params?.sort) queryParams.append("sort", params.sort);

    const queryString = queryParams.toString();
    const url = `/api/v2/orders${queryString ? `?${queryString}` : ""}`;

    return api.get(url);
  },

  exportOrders: async () => {
    return api.get("/api/v2/orders/exportOrders", { responseType: "blob" });
  },

  getOrder: async (orderId: string) => {
    return api.get(`/api/v2/orders/${orderId}`);
  },

  getorderInvoiceDownloadUrl: (orderId: string) => {
    return api.post(
      '/api/orders/generateinvoice',
      {
        orderid: orderId,
      },
    );
  },

  updateOrder: async (orderId: string, orderData: any) => {
    return api.put(`/api/orders/${orderId}`, orderData);
  },

  updateOrderVoucher: async (orderId: string, orderData: any) => {
    return api.post(`/api/v2/orders/${orderId}/vouchers/assign`, orderData);
  },

  updateOrderStatus: async (orderId: string, orderData: any) => {
    return api.patch(`/api/v2/orders/order-status/${orderId}`, orderData);
  },

  deleteOrder: async (orderId: string) => {
    return api.delete(`/api/orders/${orderId}`);
  },

  getRecentOrders: async () => {
    return api.get("/api/orders/getRecentOrders");
  },

  getMonthWiseOrders: async () => {
    return api.get("/api/orders/monthwiseorder");
  },

  getTopAgentsbyOrders: async () => {
    return api.get("/api/orders/topagentsbyorders");
  },

  getTopAgentsbyReedeemedPoints: async () => {
    return api.get("/api/orders/topagentsbyreedempoints");
  },

  getDashboardCountData: async () => {
    return api.get("/api/orders/dashboardcountdata");
  },

  uploadOrderMedia: async (formData: FormData) => {
    return api.post(
      `/api/containers/ordermedia-${Constants.USER_LOGGED_BY_CODE}/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  },

  uploadOrderComment : async (orderId: string, commentData: any) => {
    return api.post(`/api/v2/orders/${orderId}/comment`, commentData);
  },

  getOrderMediaDownloadUrl: (filename: string) => {
    return `${Constants.PY_API_URL}api/containers/ordermedia-${Constants.USER_LOGGED_BY_CODE}/download/${filename}`;
  },
  getOrderMediaDownloadUrlwithToken: (filename: string, token?: string) => {
    const tk = token ?? localStorage.getItem("accessToken");
    return `${Constants.PY_API_URL}api/containers/ordermedia-${Constants.USER_LOGGED_BY_CODE}/download/${filename}${tk ? `?access_token=${tk}` : ""}`;
  },

  deleteOrderMedia: async (ordermediaId: string) => {
    return api.delete(
      `/api/v2/orders/ordermedia/${ordermediaId}`
    );
  },

  permissionsSetting : async ()=>{
    return api.get('/api/orders/settingpermission');
  },

  updatePermissionSetting: async (payload: { key: string; value: number }) => {
    // Adjust the endpoint and method as per your backend API
    return await api.post("/api/orders/updatepermission", payload);
  },
};

//Faq API methods
export const faqAPI = {
  getFAQs: async (params?: Record<string, any>) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          queryParams.append(key, value.toString());
        }
      });
    }
    const queryString = queryParams.toString();
    const url = `/api/v2/faqs${queryString ? `?${queryString}` : ""}`;
    return api.get(url);
  },

  addFaq: async (faqData: {
    section: string;
    question: string;
    answer: string;
  }) => {
    return api.post("/api/v2/faqs", faqData);
  },

  updateFaq: async (
    faqId: string,
    faqData: { section: string; question: string; answer: string }
  ) => {
    return api.put(`/api/v2/faqs/${faqId}`, faqData);
  },

  deleteFaq: async (faqId: string) => {
    return api.delete(`/api/v2/faqs/${faqId}`);
  },
};

export default api;
